/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import Swal from 'sweetalert2';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import TableHeader from 'components/Table/TableHeader';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

import {
  MOCK_BRANCHES,
  mockListStockTakes,
  mockGetStockTake,
  mockCreateStockTake,
  mockUpdateStockTake,
  mockChangeStockTakeStatus,
  mockAddProductToStockTake,
  mockRemoveProductFromStockTake,
  mockListSessions,
} from './mockApi';

// ─── Constants ─────────────────────────────────────────────────────────────────

const STATUS_MAP = {
  draft: { label: 'ร่าง', color: 'default', icon: 'edit_note' },
  active: { label: 'กำลังนับ', color: 'warning', icon: 'inventory' },
  completed: { label: 'เสร็จสิ้น', color: 'success', icon: 'task_alt' },
  cancelled: { label: 'ยกเลิก', color: 'error', icon: 'cancel' },
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  status: 'ALL',
};

const initAttr = {
  id: '',
  code: '',
  branch_id: '',
  branch_name: '',
  remark: '',
  status: 'draft',
  products: [],
  session_count: 0,
  created_at: '',
  created_by_name: '',
};

const initAddAttr = { branch_id: '', remark: '' };

const initProductFind = {
  search: '',
  order_by: 'pd.id',
  order_sort: 'desc',
  page: 1,
  perPage: 7,
};

const initSessionFind = { page: 1, perPage: 10 };

const PROD_PER_PAGE = 10;

// ─── Component ──────────────────────────────────────────────────────────────────

function Counting() {
  const { t } = useTranslation();
  const { id } = useParams();
  const app = useSelector((state) => state[APP_STATE]);

  // List
  const [record, setRecord] = useState([]);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [find, setFind] = useState(initFind);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [showMenu, setShowMenu] = useState(null);

  // Create dialog
  const [modalAdd, setModalAdd] = useState(false);
  const [addAttr, setAddAttr] = useState(initAddAttr);
  const [confirmAdd, setConfirmAdd] = useState(false);

  // Detail master
  const [attr, setAttr] = useState(initAttr);
  const [confirmSave, setConfirmSave] = useState(false);

  // Product search modal (FetchData — same API as CouponDetail)
  const [modalProduct, setModalProduct] = useState(false);
  const [productFind, setProductFind] = useState(initProductFind);
  const [productList, setProductList] = useState([]);
  const [productPageTotal, setProductPageTotal] = useState(0);
  const [productPageNo, setProductPageNo] = useState(1);
  const [productLoading, setProductLoading] = useState(false);
  const [productOrder, setProductOrder] = useState(initProductFind.order_sort);
  const [productOrderBy, setProductOrderBy] = useState(initProductFind.order_by);
  const [productShowMenu, setProductShowMenu] = useState(null);
  const txtProductSearch = useRef(null);

  // Inline product list (detail)
  const [prodSearch, setProdSearch] = useState('');
  const [prodPage, setProdPage] = useState(1);
  const [prodShowMenu, setProdShowMenu] = useState(null);
  const [prodPerPage, setProdPerPage] = useState(PROD_PER_PAGE);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionPageTotal, setSessionPageTotal] = useState(0);
  const [sessionPageNo, setSessionPageNo] = useState(1);
  const [sessionFind, setSessionFind] = useState(initSessionFind);
  const [sessionShowMenu, setSessionShowMenu] = useState(null);

  // Shared
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState(null);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const showNotify = (msg, severity = 'success') => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotify(null)}
          severity={severity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const renderLoading = () => (
    <Backdrop sx={{ color: '#fff', zIndex: (th) => th.zIndex.drawer + 1 }} open={loading}>
      <CircularProgress color='inherit' />
    </Backdrop>
  );

  const renderStatus = (status) => {
    const s = STATUS_MAP[status] ?? { label: status, color: 'default', icon: 'help' };
    return (
      <Chip
        icon={<Icon sx={{ fontSize: '1rem !important' }}>{s.icon}</Icon>}
        label={s.label}
        color={s.color === 'default' ? undefined : s.color}
        size='small'
        variant='outlined'
      />
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // LIST PAGE
  // ═══════════════════════════════════════════════════════════════════════════════

  const listHeader = [
    { id: 'tb.id', label: 'รหัส', align: 'center', width: 70 },
    { id: 'tb.code', label: 'เลขที่ใบนับ', align: 'left', width: 130 },
    { id: 'tb.branch_name', label: 'สาขา', align: 'left' },
    { id: 'tb.remark', label: 'หมายเหตุ', align: 'left' },
    { id: 'tb.status', label: 'สถานะ', align: 'center', width: 130, sort: false },
    { id: 'tb.session_count', label: 'ครั้งนับ', align: 'center', width: 80, sort: false },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    mockListStockTakes({
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      status: data.status,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data ?? []);
        setPageTotal(result.total_pages ?? 0);
        setPageNo(result.page ?? 1);
      }
      setLoading(false);
    });
  };

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const addConfirm = () => {
    if (!addAttr.branch_id) {
      showNotify('กรุณาเลือกสาขาการนับ', 'error');
      return;
    }
    setConfirmAdd(true);
  };

  const addMaster = () => {
    setLoading(true);
    setModalAdd(false);
    setConfirmAdd(false);
    mockCreateStockTake({ branch_id: addAttr.branch_id, remark: addAttr.remark }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        window.location.replace(`/counts_round/${result.id}`);
      } else {
        setModalAdd(true);
        showNotify('เกิดข้อผิดพลาด กรุณาลองใหม่', 'error');
      }
    });
  };

  const renderListPerPageMenu = (
    <Menu
      anchorEl={showMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(showMenu)}
      onClose={() => setShowMenu(null)}
      keepMounted
    >
      {[10, 20, 50, 100].map((n) => (
        <MenuItem
          key={n}
          onClick={() => {
            view({ ...find, page: 1, perPage: n });
            setShowMenu(null);
          }}
        >
          {t('element.show_record', { num: n })}
        </MenuItem>
      ))}
    </Menu>
  );

  const renderModalAdd = () => (
    <Dialog open={modalAdd} maxWidth='xs' fullWidth keepMounted>
      <DialogTitle>
        สร้างใบนับสต็อกใหม่
        <IconButton
          onClick={() => {
            setModalAdd(false);
            setConfirmAdd(false);
          }}
          sx={{ position: 'absolute', right: 8, top: 8, color: (th) => th.palette.grey[500] }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox p={1}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                label='สาขาการนับ *'
                value={addAttr.branch_id}
                select
                variant='standard'
                onChange={(e) => setAddAttr({ ...addAttr, branch_id: e.target.value })}
              >
                <MenuItem value=''>-- เลือกสาขา --</MenuItem>
                {MOCK_BRANCHES.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='หมายเหตุ'
                fullWidth
                variant='standard'
                value={addAttr.remark}
                onChange={(e) => setAddAttr({ ...addAttr, remark: e.target.value })}
                inputProps={{ autoComplete: 'off' }}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </MDBox>
      </DialogContent>
      <DialogActions>
        {confirmAdd ? (
          <Grid container>
            <Grid item xs={6}>
              <MDButton variant='text' color='error' onClick={() => setConfirmAdd(false)}>
                <Icon>cancel</Icon>&nbsp;{t('element.cancel')}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='outlined' color='success' onClick={addMaster}>
                <Icon>save</Icon>&nbsp;{t('element.saveconfirm')}
              </MDButton>
            </Grid>
          </Grid>
        ) : (
          <MDButton variant='text' color='info' onClick={addConfirm}>
            <Icon>save</Icon>&nbsp;{t('element.save')}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );

  const renderSearch = () => (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={6} md={4}>
          <MDBox
            component='form'
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              view({ ...find, page: 1 });
            }}
          >
            <MDInput
              fullWidth
              value={find.search}
              type='search'
              onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MDInput
            fullWidth
            value={find.status}
            select
            onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
            InputProps={{
              startAdornment: <InputAdornment position='start'>สถานะ :</InputAdornment>,
            }}
          >
            <MenuItem value='ALL'>ทั้งหมด</MenuItem>
            {Object.entries(STATUS_MAP).map(([key, val]) => (
              <MenuItem key={key} value={key}>
                {val.label}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
      </Grid>
    </Card>
  );

  const renderList = () => (
    <DashboardLayout>
      <Navbar route={['คลังสินค้า', 'ใบนับสต็อก']} />
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <Tooltip title='สร้างใบนับใหม่'>
              <MDButton
                variant='contained'
                color='dark'
                size='large'
                fullWidth
                sx={{ pt: 1.6, pb: 1.6 }}
                onClick={() => {
                  setAddAttr(initAddAttr);
                  setConfirmAdd(false);
                  setModalAdd(true);
                }}
              >
                <Icon>add</Icon>
              </MDButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='stock-take-table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={listHeader}
                />
                <TableBody>
                  {record.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={listHeader.length} align='center' sx={{ py: 4 }}>
                        <MDTypography variant='caption' color='secondary'>
                          ไม่พบข้อมูล
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  )}
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>
                        <MDTypography variant='caption' color='secondary'>
                          #{row.id}
                        </MDTypography>
                      </TableCell>
                      <TableCell>
                        <NavLink to={`/counts_round/${row.id}`}>
                          <MDButton
                            variant='text'
                            color='info'
                            size='small'
                            sx={{ fontWeight: 'bold', minWidth: 0 }}
                          >
                            {row.code}
                          </MDButton>
                        </NavLink>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' fontWeight='medium'>
                          {row.branch_name}
                        </MDTypography>
                      </TableCell>
                      <TableCell>
                        <MDTypography variant='caption' color='secondary'>
                          {row.remark || '-'}
                        </MDTypography>
                      </TableCell>
                      <TableCell align='center'>{renderStatus(row.status)}</TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={row.session_count ?? 0}
                          size='small'
                          color='info'
                          icon={<Icon sx={{ fontSize: '1rem !important' }}>list_alt</Icon>}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='center'>
                <MDButton
                  variant={showMenu ? 'contained' : 'outlined'}
                  color='dark'
                  onClick={(e) => setShowMenu(e.currentTarget)}
                >
                  {t('element.show_record', { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderListPerPageMenu}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      {renderModalAdd()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // DETAIL PAGE
  // ═══════════════════════════════════════════════════════════════════════════════

  const openDetail = () => {
    setLoading(true);
    setConfirmSave(false);
    setProdSearch('');
    setProdPage(1);
    mockGetStockTake(id).then((result) => {
      if (result.status === 'ok') setAttr(result.data ?? initAttr);
      setLoading(false);
    });
  };

  const loadSessions = (data) => {
    setSessionFind(data);
    setLoading(true);
    mockListSessions({ stock_take_id: id, page: data.page, per_page: data.perPage }).then(
      (result) => {
        if (result.status === 'ok') {
          setSessions(result.data ?? []);
          setSessionPageTotal(result.total_pages ?? 0);
          setSessionPageNo(result.page ?? 1);
        }
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (id === undefined) {
      view(find);
    } else {
      openDetail();
      loadSessions(initSessionFind);
    }
  }, [id]);

  const saveMaster = () => {
    setLoading(true);
    setConfirmSave(false);
    mockUpdateStockTake(id, { branch_id: attr.branch_id, remark: attr.remark }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') showNotify('บันทึกสำเร็จ');
      else showNotify('เกิดข้อผิดพลาด', 'error');
    });
  };

  const changeStatus = (status) => {
    Swal.fire({
      title: `เปลี่ยนสถานะเป็น "${STATUS_MAP[status]?.label}"?`,
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: 'ยืนยัน',
      denyButtonText: 'ยกเลิก',
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        mockChangeStockTakeStatus(id, status).then((result) => {
          if (result.status === 'ok') {
            openDetail();
            showNotify('เปลี่ยนสถานะสำเร็จ');
          } else {
            setLoading(false);
            showNotify('เกิดข้อผิดพลาด', 'error');
          }
        });
      }
    });
  };

  const editable = attr.status === 'draft' || attr.status === 'active';

  // ─── Product search (FetchData — same endpoint as CouponDetail) ──────────────

  const searchProduct = (data) => {
    setProductFind(data);
    setProductLoading(true);
    setProductList([]);
    setProductShowMenu(null);
    FetchData('OPTIONS', '/product/search', app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    })
      .then((result) => {
        if (result.status === 'ok') {
          setProductList(result.data ?? []);
          setProductPageTotal(result.total_pages ?? 1);
          setProductPageNo(result.page ?? 1);
        }
        setProductLoading(false);
      })
      .catch(() => setProductLoading(false));
  };

  const handleProductSort = (e, property) => {
    const isAsc = productOrderBy === property && productOrder === 'asc';
    setProductOrder(isAsc ? 'desc' : 'asc');
    setProductOrderBy(property);
    searchProduct({ ...productFind, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const addProductToMaster = (product) => {
    const alreadyIn = attr.products.some(
      (p) => p.barcode_id === product.id || p.barcode_id === product.barcode_id
    );
    if (alreadyIn) {
      showNotify('สินค้านี้อยู่ในใบนับแล้ว', 'warning');
      return;
    }
    setProductLoading(true);
    mockAddProductToStockTake(id, {
      barcode_id: product.id ?? product.barcode_id,
      barcode: product.barcode,
      name: product.name,
      unit_name: product.unit_name,
      image_url: product.image_url,
    }).then((result) => {
      setProductLoading(false);
      if (result.status === 'ok') {
        showNotify('เพิ่มสินค้าสำเร็จ');
        openDetail();
      } else showNotify('เกิดข้อผิดพลาด', 'error');
    });
  };

  const removeProduct = (barcodeId) => {
    Swal.fire({
      title: 'นำสินค้าออกจากใบนับ?',
      icon: 'warning',
      showDenyButton: true,
      confirmButtonText: 'ยืนยัน',
      denyButtonText: 'ยกเลิก',
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        mockRemoveProductFromStockTake(id, barcodeId).then((result) => {
          setLoading(false);
          if (result.status === 'ok') {
            openDetail();
            showNotify('นำสินค้าออกสำเร็จ');
          } else showNotify('เกิดข้อผิดพลาด', 'error');
        });
      }
    });
  };

  // Filtered + paged inline product list
  const filteredProds = (attr.products ?? []).filter(
    (p) =>
      p.name?.toLowerCase().includes(prodSearch.toLowerCase()) ||
      p.barcode?.toLowerCase().includes(prodSearch.toLowerCase())
  );
  const prodPageTotal = Math.ceil(filteredProds.length / prodPerPage);
  const pagedProds = filteredProds.slice((prodPage - 1) * prodPerPage, prodPage * prodPerPage);

  // ─── Product search modal ─────────────────────────────────────────────────────

  const productModalHeader = [
    { id: 'pd.id', label: t('attr.image'), align: 'center', width: 60, sort: false },
    { id: 'pd.barcode', label: 'Barcode', align: 'left', width: 140 },
    { id: 'pd.name', label: 'สินค้า', align: 'left' },
    { id: 'pd.unit_id', label: 'หน่วย', align: 'left', width: 100 },
    { id: 'add', label: '', align: 'center', sort: false, width: 60 },
  ];

  const renderProductModal = () => (
    <Dialog
      open={modalProduct}
      maxWidth='xl'
      fullWidth
      keepMounted
      onClose={() => setModalProduct(false)}
    >
      <DialogTitle>
        เพิ่มสินค้าในใบนับสต็อก
        <IconButton
          onClick={() => setModalProduct(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: (th) => th.palette.grey[500] }}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox
              component='form'
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                searchProduct({ ...productFind, page: 1 });
              }}
            >
              <MDInput
                fullWidth
                value={productFind.search}
                type='search'
                onChange={(e) =>
                  setProductFind({ ...productFind, search: e.target.value, page: 1 })
                }
                InputProps={{
                  inputRef: txtProductSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='product-search-table'>
                <TableHeader
                  order={productOrder}
                  orderBy={productOrderBy}
                  onRequestSort={handleProductSort}
                  data={productModalHeader}
                />
                <TableBody>
                  {productLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : productList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ py: 6 }}>
                        <MDTypography variant='caption' color='secondary'>
                          พิมพ์ชื่อหรือบาร์โค้ดแล้วกด Enter เพื่อค้นหา
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    productList.map((row) => {
                      const alreadyIn = attr.products.some(
                        (p) => p.barcode_id === row.id || p.barcode_id === row.barcode_id
                      );
                      return (
                        <TableRow key={row.id} hover>
                          <TableCell width={50}>{Func.imageDisplay(row)}</TableCell>
                          <TableCell>
                            <MDTypography variant='caption' fontWeight='medium'>
                              {row.barcode}
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDBox>{row.code}</MDBox>
                            <MDBox>{row.name}</MDBox>
                          </TableCell>
                          <TableCell align='left'>{row.unit_name}</TableCell>
                          <TableCell align='center'>
                            {alreadyIn ? (
                              <Tooltip title='เพิ่มแล้ว'>
                                <Icon color='success'>check_circle</Icon>
                              </Tooltip>
                            ) : (
                              <IconButton color='error' onClick={() => addProductToMaster(row)}>
                                <Icon>add_circle</Icon>
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='space-between' alignItems='center'>
              <MDButton
                variant={productShowMenu ? 'contained' : 'outlined'}
                color='dark'
                onClick={(e) => setProductShowMenu(e.currentTarget)}
              >
                {t('element.show_record', { num: productFind.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              <Menu
                anchorEl={productShowMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                open={Boolean(productShowMenu)}
                onClose={() => setProductShowMenu(null)}
                keepMounted
              >
                {[7, 10, 20, 100].map((num) => (
                  <MenuItem
                    key={num}
                    onClick={() => {
                      setProductShowMenu(null);
                      searchProduct({ ...productFind, page: 1, perPage: num });
                    }}
                  >
                    {t('element.show_record', { num })}
                  </MenuItem>
                ))}
              </Menu>
              <Pagination
                count={productPageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={productPageNo}
                onChange={(e, p) => searchProduct({ ...productFind, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );

  // ─── Detail: header card ──────────────────────────────────────────────────────

  const renderDetailHeader = () => (
    <Card sx={{ p: 2 }}>
      <Grid container alignItems='center' spacing={1}>
        {/* Back button */}
        <Grid item>
          <NavLink to='/counts_round'>
            <MDButton variant='contained' color='dark' size='small'>
              <Icon>arrow_back</Icon>&nbsp;{t('element.back')}
            </MDButton>
          </NavLink>
        </Grid>

        {/* Title + status */}
        <Grid item xs>
          <MDBox display='flex' alignItems='center' gap={1.5} flexWrap='wrap'>
            <MDTypography variant='h6' fontWeight='bold'>
              {attr.code}
            </MDTypography>
            <MDTypography variant='caption' color='secondary'>
              {attr.branch_name}
              {attr.created_at ? ` · ${Func.dateTimeDisplay(attr.created_at)}` : ''}
            </MDTypography>
            {renderStatus(attr.status)}
          </MDBox>
        </Grid>

        {/* Status transitions */}
        <Grid item>
          <MDBox display='flex' gap={1} flexWrap='wrap'>
            {attr.status === 'draft' && (
              <MDButton
                variant='outlined'
                color='warning'
                size='small'
                onClick={() => changeStatus('active')}
              >
                <Icon>play_arrow</Icon>&nbsp;เริ่มนับ
              </MDButton>
            )}
            {attr.status === 'active' && (
              <MDButton
                variant='outlined'
                color='success'
                size='small'
                onClick={() => changeStatus('completed')}
              >
                <Icon>task_alt</Icon>&nbsp;เสร็จสิ้น
              </MDButton>
            )}
            {editable && (
              <MDButton
                variant='outlined'
                color='error'
                size='small'
                onClick={() => changeStatus('cancelled')}
              >
                <Icon>cancel</Icon>&nbsp;ยกเลิก
              </MDButton>
            )}
          </MDBox>
        </Grid>

        {/* Editable fields row */}
        {editable && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MDInput
                label='สาขาการนับ'
                fullWidth
                variant='standard'
                value={attr.branch_id}
                select
                onChange={(e) => setAttr({ ...attr, branch_id: e.target.value })}
              >
                <MenuItem value=''>-- เลือกสาขา --</MenuItem>
                {MOCK_BRANCHES.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <MDInput
                label='หมายเหตุ'
                fullWidth
                variant='standard'
                value={attr.remark}
                onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              display='flex'
              justifyContent='flex-end'
              alignItems='center'
              gap={1}
            >
              {confirmSave ? (
                <>
                  <MDButton
                    variant='text'
                    color='error'
                    size='small'
                    onClick={() => setConfirmSave(false)}
                  >
                    <Icon>cancel</Icon>&nbsp;{t('element.cancel')}
                  </MDButton>
                  <MDButton variant='outlined' color='success' size='small' onClick={saveMaster}>
                    <Icon>save</Icon>&nbsp;{t('element.saveconfirm')}
                  </MDButton>
                </>
              ) : (
                <MDButton
                  variant='text'
                  color='info'
                  size='small'
                  onClick={() => setConfirmSave(true)}
                >
                  <Icon>save</Icon>&nbsp;{t('element.save')}
                </MDButton>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Card>
  );

  // ─── Detail: product panel ────────────────────────────────────────────────────

  const renderProductPanel = () => (
    <Card sx={{ height: '100%' }}>
      <MDBox p={2.5}>
        {/* Toolbar */}
        <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={1}>
          <MDTypography variant='h6' fontWeight='bold'>
            สินค้าในใบนับ
            <Chip
              label={attr.products?.length ?? 0}
              size='small'
              color='info'
              sx={{ ml: 1, fontWeight: 'bold' }}
            />
          </MDTypography>
          <MDBox display='flex' gap={1} flexWrap='wrap'>
            <Tooltip title='นำเข้าจากไฟล์ (Import)'>
              <span>
                <MDButton variant='outlined' color='secondary' size='small' disabled={!editable}>
                  <Icon>upload_file</Icon>&nbsp;Import
                </MDButton>
              </span>
            </Tooltip>
            <Tooltip title='ส่งออกเป็นไฟล์ (Export)'>
              <MDButton variant='outlined' color='secondary' size='small'>
                <Icon>download</Icon>&nbsp;Export
              </MDButton>
            </Tooltip>
            {editable && (
              <MDButton
                variant='contained'
                color='dark'
                size='small'
                onClick={() => {
                  setProductFind(initProductFind);
                  setProductList([]);
                  setModalProduct(true);
                  setTimeout(() => {
                    if (txtProductSearch.current) txtProductSearch.current.focus();
                  }, 400);
                }}
              >
                <Icon>add</Icon>&nbsp;เพิ่มสินค้า
              </MDButton>
            )}
          </MDBox>
        </MDBox>

        <Divider sx={{ mb: 1.5 }} />

        {/* Inline search filter */}
        <MDBox mb={1.5}>
          <MDInput
            fullWidth
            value={prodSearch}
            type='search'
            size='small'
            onChange={(e) => {
              setProdSearch(e.target.value);
              setProdPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Icon fontSize='small'>search</Icon>
                </InputAdornment>
              ),
              autoComplete: 'off',
            }}
            placeholder='กรองสินค้า...'
          />
        </MDBox>

        {/* Table */}
        <TableContainer sx={{ maxHeight: 380 }}>
          <Table size='small' stickyHeader>
            <TableHeader
              order='asc'
              orderBy=''
              onRequestSort={() => {}}
              data={[
                { id: 'img', label: 'รูป', align: 'center', width: 52, sort: false },
                { id: 'barcode', label: 'Barcode', align: 'left', sort: false },
                { id: 'name', label: 'สินค้า', align: 'left', sort: false },
                { id: 'unit', label: 'หน่วย', align: 'center', width: 80, sort: false },
                { id: 'del', label: '', align: 'center', width: 48, sort: false },
              ]}
            />
            <TableBody>
              {pagedProds.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align='center' sx={{ py: 4 }}>
                    <MDTypography variant='caption' color='secondary'>
                      {(attr.products?.length ?? 0) === 0
                        ? 'ยังไม่มีสินค้า — กด "เพิ่มสินค้า" เพื่อเริ่มต้น'
                        : 'ไม่พบสินค้าที่ค้นหา'}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              )}
              {pagedProds.map((p) => (
                <TableRow key={p.barcode_id} hover>
                  <TableCell align='center'>
                    <Avatar
                      src={p.image_url}
                      variant='rounded'
                      sx={{ width: 32, height: 32, mx: 'auto' }}
                    >
                      <Icon fontSize='small'>inventory_2</Icon>
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant='caption' fontWeight='medium'>
                      {p.barcode}
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant='caption'>{p.name}</MDTypography>
                  </TableCell>
                  <TableCell align='center'>
                    <MDTypography variant='caption' color='secondary'>
                      {p.unit_name}
                    </MDTypography>
                  </TableCell>
                  <TableCell align='center'>
                    {editable && (
                      <Tooltip title='นำออก'>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => removeProduct(p.barcode_id)}
                        >
                          <Icon fontSize='small'>remove_circle_outline</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer: per-page + pagination */}
        <MDBox display='flex' justifyContent='space-between' alignItems='center' mt={1.5}>
          <MDButton
            variant={prodShowMenu ? 'contained' : 'outlined'}
            color='dark'
            size='small'
            onClick={(e) => setProdShowMenu(e.currentTarget)}
          >
            {prodPerPage}/หน้า&nbsp;<Icon>keyboard_arrow_down</Icon>
          </MDButton>
          <Menu
            anchorEl={prodShowMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={Boolean(prodShowMenu)}
            onClose={() => setProdShowMenu(null)}
            keepMounted
          >
            {[5, 10, 20].map((n) => (
              <MenuItem
                key={n}
                onClick={() => {
                  setProdShowMenu(null);
                  setProdPerPage(n);
                  setProdPage(1);
                }}
              >
                {n} รายการ/หน้า
              </MenuItem>
            ))}
          </Menu>
          {prodPageTotal > 1 && (
            <Pagination
              count={prodPageTotal}
              page={prodPage}
              onChange={(e, p) => setProdPage(p)}
              size='small'
              color='secondary'
            />
          )}
        </MDBox>
      </MDBox>
    </Card>
  );

  // ─── Detail: session panel ────────────────────────────────────────────────────

  const sessionListHeader = [
    { id: 'tb.id', label: 'รหัส', align: 'center', width: 70 },
    { id: 'tb.code', label: 'เลขที่', align: 'left', width: 120 },
    { id: 'tb.name', label: 'ชื่อ', align: 'left' },
    { id: 'tb.counted', label: 'นับแล้ว', align: 'center', width: 100, sort: false },
    { id: 'tb.created_at', label: 'วันที่', align: 'center', width: 100 },
    { id: 'tb.status', label: 'สถานะ', align: 'center', width: 120, sort: false },
    { id: 'action', label: '', align: 'center', width: 50, sort: false },
  ];

  const renderSessionPanel = () => (
    <Card sx={{ height: '100%' }}>
      <MDBox p={2.5}>
        {/* Toolbar */}
        <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={1}>
          <MDTypography variant='h6' fontWeight='bold'>
            ครั้งการนับ
            <Chip
              label={sessions.length}
              size='small'
              color='warning'
              sx={{ ml: 1, fontWeight: 'bold' }}
            />
          </MDTypography>
          {editable && (
            <NavLink to={`/counting_session/new?stock_take_id=${id}`}>
              <MDButton variant='contained' color='dark' size='small'>
                <Icon>add</Icon>&nbsp;สร้างครั้งนับ
              </MDButton>
            </NavLink>
          )}
        </MDBox>

        <Divider sx={{ mb: 1.5 }} />

        {/* Sessions table */}
        <TableContainer sx={{ maxHeight: 380 }}>
          <Table size='small' stickyHeader>
            <TableHeader
              order='desc'
              orderBy='tb.id'
              onRequestSort={() => {}}
              data={sessionListHeader}
            />
            <TableBody>
              {sessions.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={sessionListHeader.length} align='center' sx={{ py: 4 }}>
                    <MDTypography variant='caption' color='secondary'>
                      ยังไม่มีครั้งการนับ
                    </MDTypography>
                  </TableCell>
                </TableRow>
              )}
              {sessions.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell align='center'>
                    <MDTypography variant='caption' color='secondary'>
                      #{s.id}
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <NavLink to={`/counting_session/${s.id}`}>
                      <MDButton
                        variant='text'
                        color='info'
                        size='small'
                        sx={{ fontWeight: 'bold', minWidth: 0 }}
                      >
                        {s.code}
                      </MDButton>
                    </NavLink>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant='caption' fontWeight='medium'>
                      {s.name}
                    </MDTypography>
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={`${s.counted_products ?? 0} / ${attr.products?.length ?? 0}`}
                      size='small'
                      color='info'
                      variant='outlined'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <MDTypography variant='caption'>{Func.dateDisplay(s.created_at)}</MDTypography>
                  </TableCell>
                  <TableCell align='center'>{renderStatus(s.status)}</TableCell>
                  <TableCell align='center'>
                    <NavLink to={`/counting_session/${s.id}`}>
                      <Tooltip title='เปิดรายละเอียด'>
                        <IconButton size='small' color='info'>
                          <Icon fontSize='small'>open_in_new</Icon>
                        </IconButton>
                      </Tooltip>
                    </NavLink>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Session pagination */}
        {sessionPageTotal > 1 && (
          <MDBox display='flex' justifyContent='space-between' alignItems='center' mt={1.5}>
            <MDButton
              variant={sessionShowMenu ? 'contained' : 'outlined'}
              color='dark'
              size='small'
              onClick={(e) => setSessionShowMenu(e.currentTarget)}
            >
              {t('element.show_record', { num: sessionFind.perPage })}&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            <Menu
              anchorEl={sessionShowMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={Boolean(sessionShowMenu)}
              onClose={() => setSessionShowMenu(null)}
              keepMounted
            >
              {[10, 20, 50].map((n) => (
                <MenuItem
                  key={n}
                  onClick={() => {
                    setSessionShowMenu(null);
                    loadSessions({ ...sessionFind, page: 1, perPage: n });
                  }}
                >
                  {t('element.show_record', { num: n })}
                </MenuItem>
              ))}
            </Menu>
            <Pagination
              count={sessionPageTotal}
              variant='outlined'
              color='secondary'
              showFirstButton
              showLastButton
              page={sessionPageNo}
              onChange={(e, p) => loadSessions({ ...sessionFind, page: p })}
            />
          </MDBox>
        )}
      </MDBox>
    </Card>
  );

  // ─── Detail page ──────────────────────────────────────────────────────────────

  const renderDetail = () => (
    <DashboardLayout>
      <Navbar route={['คลังสินค้า', 'ใบนับสต็อก', attr.code || `#${id}`]} />
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderDetailHeader()}
          </Grid>
          <Grid item xs={12} lg={6}>
            {renderProductPanel()}
          </Grid>
          <Grid item xs={12} lg={6}>
            {renderSessionPanel()}
          </Grid>
        </Grid>
      </MDBox>
      {renderProductModal()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );

  return id === undefined ? renderList() : renderDetail();
}

export default Counting;
